<template>
  <div class="card">
    <div class="card__header flex justify-between items-center space-x-4">
      <p>{{ $t('ProjectSettings.ProjectTab.AssetTypeList.title') }}</p>

      <button
        v-if="projectsStore.project?.unarchived"
        type="button"
        class="btn btn--secondary-blue"
        data-test="add-asset-type"
        @click.prevent="addAssetType"
      >
        {{ $t('ProjectSettings.ProjectTab.AssetTypeList.button.add') }}
      </button>
    </div>

    <div class="flex w-full p-4 space-x-4">
      <ul
        v-if="assetTypes.length"
        class="overflow-auto space-y-4 h-64 flex-1"
      >
        <li
          v-for="(assetType) in assetTypes"
          :key="assetType.id"
          class="card__list-item"
          data-test="order-item"
        >
          <div class="flex justify-between">
            <div class="flex flex-col">
              <p>
                {{ assetType.name }}
              </p>
            </div>

            <div class="flex justify-end row-span-2 items-center">
              <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-6 h-6 hover:stroke-red-600 cursor-pointer"
                data-test="trash"
                @click="removeAssetType(assetType.id)">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>

              <button
                type="button"
                data-test="edit"
                class="mr-2 btn-icon btn-icon-blue"
                @click="editAssetType(assetType)"
              >
                <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="h-5 w-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
              </button>
            </div>
          </div>
        </li>
      </ul>

      <div
        v-else
        class="text-center font-semibold w-full p-2"
      >
        {{ $t('ProjectSettings.ProjectTab.AssetTypeList.messages.noTypesFound') }}
      </div>
    </div>

    <AssetTypeModal
      v-if="show"
      :asset-type="selectedAssetType"
      :project-id="props.projectId"
      @close="show = false"
    />
  </div>
</template>

<script setup lang="ts">
import { t } from '@/i18n';
import AssetType from '@/models/AssetType';
import { useAssetTypesStore } from '@/store/assetTypes';
import { useProjectsStore } from '@/store/projects';
import Swal from 'sweetalert2';
import { computed, ref } from 'vue';
import AssetTypeModal from './AssetTypeModal.vue';

const props = defineProps<{
  projectId: string;
}>();

const assetTypesStore = useAssetTypesStore();

const removeAssetType = (id: Id) => {
  Swal.fire({
    icon: 'warning',
    title: t('ProjectSettings.ProjectTab.AssetTypeList.confirm.removeTitle'),
    showConfirmButton: true,
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      assetTypesStore.deleteAssetType(id).catch(() => {
        Swal.fire({
          icon: 'error',
          title: t('ProjectSettings.ProjectTab.AssetTypeList.message.deleteFailed'),
        });
      });
    }
  });
};

const show = ref(false);
const selectedAssetType = ref();

const editAssetType = (assetType: AssetType) => {
  selectedAssetType.value = assetType;
  show.value = true;
};

const assetTypes = computed(() => {
  return assetTypesStore.models.where('project_id', parseInt(props.projectId)).get();
});

const addAssetType = () => {
  selectedAssetType.value = undefined;
  show.value = true;
};

const projectsStore = useProjectsStore();
</script>

<style scoped></style>
